import type { ReactElement } from 'react';
import type { MaterialItem } from '../../domain/procurementRequest';
import { materialLineDetailRows } from '../../domain/procurementRequest';
import type { RfqMaterialLineQuoteRow, RfqVendorInvitationRow } from '../../domain/types';
import { vendorHasQuote } from './rfqVendorQuotesOrder';

/** Whole USD amounts for comparison tables. */
function formatUsdWhole(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(n);
}

function lineForVendor(v: RfqVendorInvitationRow, itemId: string) {
  return v.lineQuotes?.find((l) => l.itemId === itemId);
}

export interface RfqVendorQuotesMaterialTablesProps {
  materials: MaterialItem[];
  orderedInvited: RfqVendorInvitationRow[];
  onOpenAttachment: (vendorId: string, fileName: string) => void;
}

function requirementLabel(m: MaterialItem): string {
  const rows = materialLineDetailRows(m);
  const desc = rows.find((r) => r.label === 'Description');
  const name = rows.find((r) => r.label === 'Name');
  const bits = [name?.value, desc?.value].filter(Boolean) as string[];
  return bits.join(' — ') || m.description || m.name || 'Requirement';
}

export function RfqVendorQuotesMaterialTables({
  materials,
  orderedInvited,
  onOpenAttachment
}: RfqVendorQuotesMaterialTablesProps): ReactElement {
  return (
    <>
      {materials.map((mat) => {
        const itemId = mat.itemId?.trim();
        if (!itemId) {
          return null;
        }
        const req = requirementLabel(mat);

        return (
          <section key={itemId} className="rfq-comparison-block">
            <table className="rfq-comparison-req-table">
              <tbody>
                <tr>
                  <th scope="row">Requirement</th>
                  <td>{req}</td>
                </tr>
              </tbody>
            </table>

            <div className="rfq-comparison-scroll">
              <table className="rfq-comparison-grid">
                <thead>
                  <tr>
                    <th scope="col" className="rfq-comparison-label-col" />
                    {orderedInvited.map((v) => (
                      <th key={v.vendorId} scope="col" className="rfq-comparison-vendor-col">
                        {v.vendorName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ['Mill', (l: RfqMaterialLineQuoteRow | undefined) => l?.mill],
                      ['Country of Origin', (l: RfqMaterialLineQuoteRow | undefined) => l?.countryOfOrigin],
                      ['Required Qty (ft.)', (l: RfqMaterialLineQuoteRow | undefined) => l?.requiredQtyFt],
                      ['Price/Ft.', (l: RfqMaterialLineQuoteRow | undefined) => l?.unitPrice],
                      ['Total Pipe Cost', (l: RfqMaterialLineQuoteRow | undefined) => l?.totalPipeCost],
                      ['No. of Truckloads', (l: RfqMaterialLineQuoteRow | undefined) => l?.truckloadCount],
                      ['Cost of 1 Truckload', (l: RfqMaterialLineQuoteRow | undefined) => l?.costPerTruckload],
                      ['Trucking Cost', (l: RfqMaterialLineQuoteRow | undefined) => l?.truckingCost],
                      ['Total Cost', (l: RfqMaterialLineQuoteRow | undefined) => l?.totalLineCost]
                    ] as const
                  ).map(([label, getVal]) => (
                    <tr key={label} className={label === 'Total Cost' ? 'rfq-comparison-strong' : undefined}>
                      <th scope="row" className="rfq-comparison-label-col">
                        {label}
                      </th>
                      {orderedInvited.map((v) => {
                        const l = lineForVendor(v, itemId);
                        const raw = vendorHasQuote(v) && l ? getVal(l) : undefined;
                        const isMoney =
                          label.includes('Cost') ||
                          label.includes('Price') ||
                          label === 'Cost of 1 Truckload';
                        let cell: string;
                        if (raw === undefined || raw === null || raw === '') {
                          cell = '—';
                        } else if (typeof raw === 'number') {
                          cell = isMoney ? formatUsdWhole(raw) : String(raw);
                        } else {
                          cell = String(raw);
                        }
                        return (
                          <td key={v.vendorId} className={label === 'Total Cost' ? 'rfq-comparison-strong' : undefined}>
                            {cell}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="rfq-comparison-attach-row">
                    <th scope="row" className="rfq-comparison-label-col">
                      Attached document
                    </th>
                    {orderedInvited.map((v) => (
                      <td key={v.vendorId}>
                        {vendorHasQuote(v) && v.attachmentBlobUrl && v.attachmentFileName ? (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => onOpenAttachment(v.vendorId, v.attachmentFileName ?? 'attachment.pdf')}
                          >
                            {v.attachmentFileName}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </>
  );
}
